import { Audiobook, Chapter } from '../types';
import { api } from './api';
import axios from 'axios';
import config from 'config/config';

const importApi = axios.create({
  baseURL: config.CORESKILLS_API_URL,
});

importApi.interceptors.request.use((config) => {
  if (config.headers) {
    config.headers['x-api-key'] = process.env.REACT_APP_CORESKILLS_API_KEY;
  }
  return config;
});

const API_PATHS = Object.freeze({
  audiobooks: {
    audiobooks: '/audiobooks',
    id: '/audiobooks/{id}',
    recalculateDuration: '/audiobooks/{id}/recalculate-duration',
    listForImport: '/api/audiobooks/ajassa/list',
    detailsForImport: '/api/audiobooks/ajassa/details/{id}',
  },
});

const READ_ONLY_AUDIOBOOK_FIELDS = new Set(['chapters', 'totalDurationSeconds', 'calculatedTotalDurationSeconds']);

const appendAudiobookFields = (formData: FormData, audiobook: Audiobook) => {
  Object.keys(audiobook)
    .filter((key) => !READ_ONLY_AUDIOBOOK_FIELDS.has(key))
    .forEach((key) => {
      const value = audiobook[key as keyof Audiobook];

      if (key === 'isTimeTracked') {
        formData.append(key, value ? '1' : '0');
        return;
      }

      if (value !== null && value !== undefined) {
        formData.append(key, value.toString());
      }
    });
};

// Audiobook
export const getAudiobooks = (): Promise<Audiobook[]> => api.get(API_PATHS.audiobooks.audiobooks, {});

export const getAudiobooksForImport = (): Promise<Audiobook[]> => importApi.get(API_PATHS.audiobooks.listForImport, {});

export const getAudiobookDetailsForImport = (id: string): Promise<Audiobook> =>
  importApi.get(API_PATHS.audiobooks.detailsForImport.replace('{id}', id), {});

export const getSingleAudiobook = (id: string): Promise<Audiobook> =>
  api.get(API_PATHS.audiobooks.id.replace('{id}', id), {});

export const addSingleAudiobook = (
  audiobook: Audiobook,
  chapters: Chapter[],
  files: File[],
  imageFile?: File | string,
  ebookFile?: File | string,
  pdfBookFile?: File | string
): Promise<Audiobook> => {
  const formData = new FormData();

  appendAudiobookFields(formData, audiobook);

  // Handle chapters - send URL for imported, file for new
  chapters?.forEach((chapter) => formData.append('chapters', JSON.stringify(chapter)));

  // Handle newly uploaded chapter files
  files?.forEach((file) => {
    formData.append('files', file, file.name.normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
  });

  // Handle cover image, ebook, pdf - send URL for imported, file for new
  if (imageFile) {
    if (typeof imageFile === 'string') {
      formData.append('image', imageFile);
    } else {
      formData.append('image', imageFile as File);
    }
  }
  if (ebookFile) {
    if (typeof ebookFile === 'string') {
      formData.append('ebook', ebookFile);
    } else {
      formData.append('ebook', ebookFile as File);
    }
  }
  if (pdfBookFile) {
    if (typeof pdfBookFile === 'string') {
      formData.append('pdfBook', pdfBookFile);
    } else {
      formData.append('pdfBook', pdfBookFile as File);
    }
  }

  return api.post(API_PATHS.audiobooks.audiobooks, formData, { timeout: 360000 });
};

export const updateSingleAudiobook = (
  audiobook: Audiobook,
  chapters: Chapter[],
  files: File[],
  imageFile?: File,
  ebookFile?: File,
  pdfBookFile?: File
): Promise<Audiobook> => {
  const formData = new FormData();

  // Handle chapters and files
  chapters?.forEach((chapter) => formData.append('chapters', JSON.stringify(chapter)));
  files?.forEach((file) => {
    formData.append('files', file, file.name.normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
  });

  appendAudiobookFields(formData, audiobook);

  // Handle file uploads (image, ebook, pdf)
  if (imageFile) {
    formData.append('image', imageFile);
  }
  if (ebookFile) {
    formData.append('ebook', ebookFile);
  }
  if (pdfBookFile) {
    formData.append('pdfBook', pdfBookFile);
  }

  const url = API_PATHS.audiobooks.id.replace('{id}', audiobook.id!.toString());
  return api.put(url.split(':')[0], formData, { timeout: 360000 });
};

export const deleteSingleAudiobook = (id: string): Promise<void> => {
  return api.delete(API_PATHS.audiobooks.id.replace('{id}', id), {});
};

export const recalculateAudiobookDuration = (id: string): Promise<Audiobook> => {
  return api.post(API_PATHS.audiobooks.recalculateDuration.replace('{id}', id), {});
};
